import MaterialTable from '@material-table/core';
import { DeleteOutlineOutlined, EditOutlined } from '@mui/icons-material';
import { Box, Card, CardContent } from '@mui/material';
import { generateClient } from 'aws-amplify/api';
import { enqueueSnackbar } from 'notistack';
import { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ConfirmDialog } from '../../components/confirmDialog/confirmDialog';
import { PageAppBar } from '../../components/pageAppBar';
import { UserContext } from '../../contexts/UserContext';
import { createAuditLog, deleteCallerIdConfiguration } from '../../graphql/mutations';
import { callerIdConfigurationsByTenant } from '../../graphql/queries';
import { cardStyle } from '../../theme';

export function CallerIdConfigurations() {
    const client = generateClient();
    const userContext = useContext(UserContext);
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const [configurations, setConfigurations] = useState([]);
    const [toDelete, setToDelete] = useState(null);
    const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

    useEffect(() => {
        async function getData() {
            // Retrieving configurations
            setLoading(true);
            await getConfigurations();
        }
        if (userContext.tenantId) {
            getData();
        }
    }, [userContext.tenantId]);

    useEffect(() => {
        setLoading(false);
    }, [configurations]);

    async function getConfigurations() {
        const savedConfigurations = await client.graphql({
            query: callerIdConfigurationsByTenant,
            variables: { tenant: userContext.tenantId },
        });
        if (savedConfigurations && savedConfigurations.data) {
            setConfigurations(
                savedConfigurations.data.callerIdConfigurationsByTenant.items.sort(
                    (a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt),
                ),
            );
        }
    }

    async function handleDelete() {
        try {
            setLoading(true);

            await client.graphql({
                query: deleteCallerIdConfiguration,
                variables: { input: { id: toDelete } },
            });
            await client.graphql({
                query: createAuditLog,
                variables: {
                    input: {
                        tenant: userContext.tenantId,
                        type: 'CallerIdConfiguration',
                        timestamp: new Date().toISOString(),
                        userName: userContext.email,
                        documentId: toDelete,
                        name: configurations.find(config => config.id === toDelete).name,
                        currentValue: "{}",
                        action: 'DELETED',
                        ttl: Math.floor(Date.now() / 1000) + (6 * 30 * 24 * 60 * 60), //6 months
                    }
                }
            });
            setToDelete(null);
            setConfirmDeleteOpen(false);
            enqueueSnackbar('Profile deleted successfully.', {
                variant: 'success',
                autoHideDuration: 2000
            });
            await getConfigurations();
            setLoading(false);

        } catch (e) {
            console.error(e);
            enqueueSnackbar('Error deleting profile.', {
                variant: 'error',
                autoHideDuration: 2000
            });
            setLoading(false);
            setToDelete(null);
            setConfirmDeleteOpen(false);
        }
    }

    function handleCancelDelete() {
        setToDelete(null);
        setConfirmDeleteOpen(false);
    }

    return (
        <Box>
            <PageAppBar
                title="Caller ID Configurations"
                // description="Set up Call Windows, Cadence, Attempts, and More"
                actionOneText={userContext.securityOptions.communicationProfiles > 1 &&  "+ Configuration"}
                actionOneHandler={() => {
                        navigate('/caller-id/configurations/new');
                }}
                actionOneDisabled={userContext.securityOptions.communicationProfiles < 2}
            />
            <Card variant='outlined' sx={cardStyle}>
                <CardContent>
                    <MaterialTable
                        components={{
                            Container: (props) => <Box {...props} elevation={0} />,
                        }}
                        title=""
                        data={configurations}
                        columns={[
                            { title: 'Name', field: 'name' }
                        ]}
                        isLoading={loading}
                        options={{
                            actionsColumnIndex: -1,
                            pageSize: 10,
                            searchFieldStyle: {
                                marginBottom: '16px',
                                marginLeft: '-28px',
                            },
                            headerStyle: {
                                borderTop: '1px solid #e0e0e0',
                                padding: '16px',
                            },
                            searchFieldVariant: 'outlined',
                            paginationType: 'stepped',
                        }}
                        actions={[
                            {
                                disabled: userContext.securityOptions.communicationProfiles < 2,
                                icon: () => <EditOutlined color="primary" />,
                                tooltip: 'Edit Profile',
                                onClick: (event, rowData) => {
                                    navigate(`/caller-id/configurations/${rowData.id}`);
                                },
                            },
                            rowData => function () {
                                const disabled = userContext.securityOptions.communicationProfiles < 2;
                                return {
                                    icon: () => <DeleteOutlineOutlined color={disabled ? 'disabled' : 'primary'} />,
                                    tooltip: 'Delete Profile',
                                    onClick: (event, rowData) => {
                                        setToDelete(rowData.id);
                                        setConfirmDeleteOpen(true);
                                    },
                                    disabled
                                }
                            }(),
                        ]}
                        onRowClick={(event, rowData) => {
                            navigate(`/caller-id/configurations/${rowData.id}`);
                        }}
                    />
                </CardContent>
            </Card>
            <ConfirmDialog
                open={confirmDeleteOpen}
                title="Delete Profile"
                description="Are you sure you want to delete this profile?"
                actionOneText="Delete"
                actionOneHandler={() => {
                    handleDelete();
                }}
                actionTwoText="Cancel"
                actionTwoHandler={() => {
                    handleCancelDelete();
                }}
            />
        </Box>
    );
}
